import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { WHY_WORDS } from "../lib/about";
import { fadeUp, viewportOnce, EASE } from "../lib/motion";

const ConvergenceSvg = () => (
    <svg
        viewBox="0 0 200 420"
        fill="none"
        className="h-full w-full"
        aria-hidden="true"
    >
        {WHY_WORDS.map((_, i) => {
            const y = 66 + i * 96;
            return (
                <motion.path
                    key={i}
                    d={`M0 ${y} C 90 ${y}, 110 210, 200 210`}
                    stroke="rgba(255,255,255,0.28)"
                    strokeWidth="1"
                    initial={{ pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    viewport={viewportOnce}
                    transition={{ duration: 1.1, ease: EASE, delay: 0.2 + i * 0.14 }}
                />
            );
        })}
        <motion.circle
            cx="200"
            cy="210"
            r="4"
            fill="#FF5B00"
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={viewportOnce}
            transition={{ duration: 0.5, delay: 1.3 }}
        />
    </svg>
);

const WhyClarity = () => (
    <section
        data-testid="why-clarity"
        className="relative border-y border-white/10 bg-surface/40 py-24 lg:py-36"
    >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="flex items-center gap-6"
            >
                <p className="font-mono text-[13px] tracking-[0.3em] text-mist">
                    WHY BUILDEASE EXISTS
                </p>
                <span className="h-px flex-1 bg-white/10" />
                <p className="font-mono text-[13px] tracking-[0.3em] text-cad">
                    C.01 — C.04
                </p>
            </motion.div>

            <motion.h2
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                data-testid="why-headline"
                className="mt-12 max-w-4xl font-display text-4xl font-semibold leading-[1.1] text-white md:text-6xl"
            >
                Bringing clarity to an unnecessarily complicated industry
                <span className="text-accent">.</span>
            </motion.h2>

            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="mt-10 grid max-w-4xl gap-6 md:grid-cols-2"
            >
                <p className="text-[16px] leading-relaxed text-mist">
                    Residential construction involves numerous decisions,
                    professionals, documents, costs and responsibilities. For
                    homeowners without construction experience, understanding
                    whether a contractor's quotation is reasonable, what
                    materials are required, how much a project could cost or
                    what compliance requirements apply can be difficult.
                </p>
                <p className="text-[16px] leading-relaxed text-mist">
                    Construction information is often fragmented across
                    different professionals, suppliers and service providers.
                    This creates uncertainty at a time when homeowners need
                    clarity most.
                </p>
            </motion.div>

            <div className="mt-16 grid items-center gap-12 lg:grid-cols-12">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="lg:col-span-5"
                >
                    {WHY_WORDS.map((w, i) => (
                        <p
                            key={w}
                            data-testid={`why-challenge-${i + 1}`}
                            className="flex items-baseline justify-between border-t border-white/10 py-4 first:border-t-0 first:pt-0 lg:first:border-t lg:first:pt-4"
                        >
                            <span className="font-display text-xl font-medium tracking-wide text-white md:text-2xl">
                                {w}
                            </span>
                            <span className="font-mono text-[13px] text-cad">
                                C.0{i + 1}
                            </span>
                        </p>
                    ))}
                    <div className="mt-6 flex justify-center lg:hidden">
                        <ArrowDown className="h-5 w-5 text-accent" />
                    </div>
                </motion.div>

                <div
                    className="hidden h-[420px] lg:col-span-2 lg:block"
                    aria-hidden="true"
                >
                    <ConvergenceSvg />
                </div>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="lg:col-span-5"
                >
                    <div
                        data-testid="clarity-node"
                        className="relative border border-accent/70 bg-ink/60 px-8 py-14 text-center md:py-20"
                    >
                        <span className="absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 bg-accent" />
                        <p className="font-mono text-[13px] tracking-[0.3em] text-mist">
                            THE RESULT
                        </p>
                        <p className="mt-5 font-display text-4xl font-semibold tracking-tight text-white md:text-6xl">
                            CLARITY
                            <span className="text-accent">.</span>
                        </p>
                        <p className="mt-5 font-mono text-[13px] tracking-[0.25em] text-mist">
                            PLAN — COST — MATERIAL — QUOTE — COMPLIANCE —
                            PROJECT
                        </p>
                    </div>
                </motion.div>
            </div>

            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="mt-16 grid max-w-4xl gap-6 border-t border-white/10 pt-10 md:grid-cols-2"
            >
                <p className="text-[16px] leading-relaxed text-mist">
                    BuildEase was conceived to make construction information
                    more accessible, connected and understandable. We believe
                    homeowners should not need to become construction experts
                    simply to make informed decisions about building their own
                    homes.
                </p>
            </motion.div>
        </div>
    </section>
);

export default WhyClarity;
